import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "../../../app/AppContext";
import { InlineApiError, ModalOverlay } from "../../../shared/Shared";
import { useCatalogFacets, useCatalogTemplate, useTemplateYaml } from "../catalog-hooks";
import { useCreateFromTemplate } from "../use-template";
import { facetLabels } from "./TemplateDetailFacets";
import { TemplateDetailView } from "./TemplateDetailView";

/**
 * Canonical member template detail at /templates/:libraryKey/:slug. Reached from a gallery
 * card it renders as the V1 modal OVER the gallery (the card pushes this route with the
 * gallery as background location) and starts from the gallery's snapshot row, so it opens
 * without a request; opened directly or refreshed it renders the same card as a standalone
 * page. Signed out the public tree renders PublicTemplateDetailPage under the same query key.
 */
export function TemplateDetailRoute({ variant }: { variant: "modal" | "standalone" }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const params = useParams<{ libraryKey: string; slug: string }>();
  const { locale } = useAppContext();
  const libraryKey = params.libraryKey ?? "";
  const slug = params.slug ?? "";

  const detailQuery = useCatalogTemplate(libraryKey, slug, locale);
  const { facets } = useCatalogFacets(locale);
  const labels = useMemo(() => facetLabels(facets), [facets]);
  const template = detailQuery.data?.template;
  const yamlQuery = useTemplateYaml(template?.templateVersionId);
  const create = useCreateFromTemplate();
  // "Use this template" pins the project to the EXACT displayed version.
  const onUse = () => {
    if (!template || create.isPending) return;
    create.mutate({ name: template.name, templateVersionId: template.templateVersionId });
  };

  const view = template ? (
    <TemplateDetailView
      template={template}
      labels={labels}
      yaml={yamlQuery.data?.yaml ?? null}
      onUse={onUse}
      usePending={create.isPending}
      {...(variant === "modal" ? { onClose: () => navigate(-1) } : {})}
      error={create.error ?? yamlQuery.error}
    />
  ) : null;

  if (variant === "modal") {
    return (
      <ModalOverlay
        ariaCloseLabel={intl.formatMessage({ id: "common.close" })}
        onClose={() => navigate(-1)}
        zIndex={50}
      >
        <InlineApiError error={detailQuery.error} />
        {view}
      </ModalOverlay>
    );
  }
  return (
    <div
      style={{
        maxWidth: "1120px",
        width: "100%",
        margin: "0 auto",
        padding: "34px clamp(16px, 4vw, 28px) 60px",
        boxSizing: "border-box",
      }}
    >
      <Link
        to="/templates"
        style={{
          display: "inline-block",
          marginBottom: "16px",
          fontFamily: "var(--brand-font-display)",
          fontWeight: 800,
          fontSize: "13px",
          color: "var(--brand-ink)",
        }}
      >
        ← <FormattedMessage id="catalog.back" />
      </Link>
      {detailQuery.isLoading ? (
        <p>
          <FormattedMessage id="common.loading" />
        </p>
      ) : null}
      <InlineApiError error={detailQuery.error} />
      {view}
    </div>
  );
}
